import type { MCPServerType } from "@app/lib/api/mcp";
import type { SkillPermissionFilteringMode } from "@app/lib/resources/skill/skill_resource";
import type {
  SkillAvailability,
  SkillListItemType,
  SkillStatus,
  SkillType,
  SkillWithoutInstructionsAndToolsType,
  SkillWithRelationsType,
} from "@app/types/assistant/skill_configuration";
import type { SpaceKind } from "@app/types/space";
import type { UserType } from "@app/types/user";

export type GetSkillsReinforcementSettingsResponseBody = {
  skills: (SkillWithoutInstructionsAndToolsType & {
    editors: UserType[] | null;
  })[];
};

export type SkillSearchPermissionFiltering = Exclude<
  SkillPermissionFilteringMode,
  "dangerously_skip"
>;

// OR within a dimension, AND across dimensions. Selection never replaces ACLs.
export interface SkillSearchFilters {
  // Omitted means active only. Suggested skills are never searchable.
  status?: Extract<SkillStatus, "active" | "archived">[];
  mcpServerViewIds?: string[];
  // Supports "edited by me", but not "not edited by me".
  editedByMe?: true;
  availability?: SkillAvailability[];
  // True selects code-defined skills, false selects workspace skills, omitted selects both.
  codeDefinedOnly?: boolean;
  editorIds?: string[];
  childSkillIds?: string[];
  spaceIds?: string[];
  // Inclusive bounds; skills without usage never match.
  activeUsersCount?: { min?: number; max?: number };
}

export const SKILL_SEARCH_TERMS_FACETS = [
  "availability",
  "editors",
  "childSkills",
  "spaces",
  "mcpServerViews",
] as const;
export type SkillSearchTermsFacet = (typeof SKILL_SEARCH_TERMS_FACETS)[number];

export const SKILL_SEARCH_FACETS = [
  ...SKILL_SEARCH_TERMS_FACETS,
  "usage",
] as const;
export type SkillSearchFacet = (typeof SKILL_SEARCH_FACETS)[number];

export interface SkillSearchFacetValue {
  value: string;
  count: number;
}

// Bounds of a numeric facet; null when no matching skill holds a value.
export interface SkillSearchRangeFacetValue {
  min: number | null;
  max: number | null;
}

// Distinct indexed values of each requested facet, with the number of matching skills holding each.
export type SkillSearchFacetValues = Partial<
  Record<SkillSearchTermsFacet, SkillSearchFacetValue[]>
> & { usage?: SkillSearchRangeFacetValue };

export const SKILL_SEARCH_SORTS = [
  "relevance",
  "usage",
  "name",
  "updatedAt",
] as const;
export type SkillSearchSort = (typeof SKILL_SEARCH_SORTS)[number];

export const SKILL_SEARCH_SORT_ORDERS = ["asc", "desc"] as const;
export type SkillSearchSortOrder = (typeof SKILL_SEARCH_SORT_ORDERS)[number];

export const SKILL_SEARCH_SELECTION_MODES = [
  "favorites_only",
  "favorites_or_all",
  "all",
] as const;
export type SkillSearchSelectionMode =
  (typeof SKILL_SEARCH_SELECTION_MODES)[number];

export type SearchSkillsResponseBody = {
  skills: (SkillListItemType & { isFavorite: boolean })[];
  total: number;
  hasMore: boolean;
  // Whether this result is restricted to favorites, rather than ordinary search.
  isFavoritesOnly: boolean;
  facets: {
    availability?: { availability: SkillAvailability; count: number }[];
    editors?: (Pick<UserType, "sId" | "fullName" | "image"> & {
      count: number;
    })[];
    childSkills?: {
      sId: string;
      name: string;
      icon: string | null;
      count: number;
    }[];
    spaces?: { sId: string; name: string; kind: SpaceKind; count: number }[];
    mcpServerViews?: {
      sId: string;
      mcpServerId: string;
      name: string;
      icon: MCPServerType["icon"];
      count: number;
    }[];
    usage?: SkillSearchRangeFacetValue;
  };
};

export type PostSkillResponseBody = {
  skill: SkillType;
};

export type GetReinforcementDailySpendResponseBody = {
  // ISO date strings ("YYYY-MM-DD") → spend in microUSD for that day.
  dailySpendMicroUsd: Record<string, number>;
  // ISO date strings ("YYYY-MM-DD") → spend in AWU credits for that day
  // (margin included, as billed to Metronome).
  dailySpendAwuCredits: Record<string, number>;
  periodStartDate: string;
  periodEndDate: string;
};

export type GetSkillResponseBody = {
  skill: SkillType & { isFavorite: boolean };
};

export type GetSkillWithRelationsResponseBody = {
  skill: SkillWithRelationsType & { isFavorite: boolean };
};

export type PatchSkillResponseBody = {
  skill: Omit<
    SkillType,
    | "author"
    | "requestedSpaceIds"
    | "workspaceId"
    | "createdAt"
    | "updatedAt"
    | "editedBy"
  >;
};

export type DeleteSkillResponseBody = {
  success: boolean;
};

export type GetSkillsSpendResponseBody = {
  // Map from skill sId to total spent in the current billing period (microUSD).
  // Skills with no usage in the period are omitted.
  spentMicroUsdBySkillId: Record<string, number>;
  // Map from skill sId to total spent in the current billing period in AWU
  // credits (margin included, as billed to Metronome). Skills with no usage
  // in the period are omitted.
  spentAwuCreditsBySkillId: Record<string, number>;
};
